import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  hoursForDate,
  computeAvailableSlots,
  formatWallMin,
  parseWallMin,
} from '../lib/availability';
import { useConfirm } from './ConfirmDialog';

/**
 * RescheduleModal — shop-taken phone reschedule flow.
 *
 * Shows the target barber's real availability (barber_hours minus
 * existing bookings), defaulting to the booking's current date. Barber
 * and time are both editable; service, price, and payment state stay
 * attached to the booking (backend handles that).
 *
 * On save:
 *   1. Confirm dialog names the specific booking and new time.
 *   2. PATCH /api/bookings/{id}/reschedule with { new_barber_id, new_starts_at }.
 *   3. On 409 (slot taken by another concurrent booking), surface a
 *      clear message so the shop can pick again.
 *   4. On success: fire onSaved(), close.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SLOT_STEP_MIN = 15;

const fmtMoney = (n) => `£${Number(n || 0).toFixed(0)}`;
const fmtDate = (d) => d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23,59,59,999); return x; };
const addDays    = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };

export const RescheduleModal = ({ booking, onClose, onSaved }) => {
  const [confirmNode, confirm] = useConfirm();
  const shopId = booking?.shop_id;
  const durationMin = Number(booking?.service?.duration_min || 30);
  const initialStart = new Date(booking.starts_at);

  const [barbers, setBarbers] = React.useState([]);
  const [hoursByBarber, setHoursByBarber] = React.useState({});
  const [selectedBarberId, setSelectedBarberId] = React.useState(booking.barber_id);
  const [selectedDate, setSelectedDate] = React.useState(startOfDay(initialStart));
  const [dayBookings, setDayBookings] = React.useState([]);
  const [selectedStartMin, setSelectedStartMin] = React.useState(
    initialStart.getHours() * 60 + initialStart.getMinutes()
  );
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Load barbers + their weekly hours once.
  React.useEffect(() => {
    if (!shopId) return;
    (async () => {
      const { data: bb } = await supabase
        .from('barbers')
        .select('id,name,slot_interval_minutes,booking_buffer_minutes')
        .eq('shop_id', shopId)
        .eq('active', true)
        .order('created_at');
      setBarbers(bb || []);
      if (bb?.length) {
        const { data: hh } = await supabase
          .from('barber_hours')
          .select('barber_id,weekday,start_time,end_time,is_open')
          .in('barber_id', bb.map(b => b.id));
        const map = {};
        (hh || []).forEach(r => {
          if (!map[r.barber_id]) map[r.barber_id] = [];
          map[r.barber_id].push(r);
        });
        setHoursByBarber(map);
      }
    })();
  }, [shopId]);

  // Load bookings for the selected barber + date so the slot list
  // knows what is already taken. Excludes the booking we are moving.
  React.useEffect(() => {
    if (!shopId || !selectedBarberId || !selectedDate) return;
    (async () => {
      const from = startOfDay(selectedDate);
      const to   = endOfDay(selectedDate);
      const { data } = await supabase
        .from('bookings')
        .select('id,starts_at,ends_at,status')
        .eq('shop_id', shopId)
        .eq('barber_id', selectedBarberId)
        .gte('starts_at', from.toISOString())
        .lte('starts_at', to.toISOString())
        .neq('id', booking.id)                   // ignore the booking we are rescheduling
        .neq('status', 'cancelled');             // cancelled slots are free
      setDayBookings(data || []);
    })();
  }, [shopId, selectedBarberId, selectedDate, booking.id]);

  const hours = hoursForDate(hoursByBarber, selectedBarberId, selectedDate);

  // Available slot starts for the chosen barber+date+duration.
  const availableStarts = React.useMemo(() => {
    if (!hours.open) return [];
    const barber = barbers.find(b => b.id === selectedBarberId);
    return computeAvailableSlots({
      hours,
      durationMin,
      bookings: dayBookings,
      stepMin: SLOT_STEP_MIN,
      bufferMin: barber?.booking_buffer_minutes || 0,
    });
  }, [hours, dayBookings, durationMin, barbers, selectedBarberId]);

  // If the currently selected time isn't available (different barber
  // or day), snap to the first available.
  React.useEffect(() => {
    if (availableStarts.length === 0) return;
    if (!availableStarts.includes(selectedStartMin)) {
      setSelectedStartMin(availableStarts[0]);
    }
  }, [availableStarts, selectedStartMin]);

  const goToday   = () => setSelectedDate(startOfDay(new Date()));
  const goPrevDay = () => setSelectedDate(addDays(selectedDate, -1));
  const goNextDay = () => setSelectedDate(addDays(selectedDate, +1));

  const submit = async () => {
    setError(null);
    const targetBarber = barbers.find(b => b.id === selectedBarberId);
    if (!targetBarber) { setError('Pick a barber.'); return; }
    if (!availableStarts.includes(selectedStartMin)) {
      setError('Pick an available time.');
      return;
    }
    // Build the new_starts_at as an ISO string in the browser's local tz.
    const newStart = new Date(selectedDate);
    newStart.setHours(Math.floor(selectedStartMin / 60), selectedStartMin % 60, 0, 0);

    // Confirm dialog naming the booking + new time.
    const oldDate = new Date(booking.starts_at);
    const isMove = newStart.getTime() !== oldDate.getTime()
                  || selectedBarberId !== booking.barber_id;
    if (!isMove) { onClose?.(); return; }

    const clientName = booking.customer?.name || 'this client';
    const svcName = booking.service?.name || 'appointment';
    const ok = await confirm({
      title: `Reschedule ${clientName}\u2019s ${svcName}?`,
      body: `New time: ${fmtDate(selectedDate)} at ${formatWallMin(selectedStartMin)} with ${targetBarber.name}. The client will be sent an SMS.`,
      confirmLabel: 'Reschedule',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;

    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/bookings/${booking.id}/reschedule`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          new_barber_id: selectedBarberId,
          new_starts_at: newStart.toISOString(),
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        if (res.status === 409) {
          setError('That slot was just taken. Pick another time.');
        } else {
          setError(`Reschedule failed: ${t}`);
        }
        setBusy(false);
        return;
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Reschedule failed.');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      data-testid="reschedule-modal"
      style={{ zIndex: 9200 }} /* sits above the booking detail panel (8500) but below confirm dialog (10000) */
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div className="modal-card" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <h3 style={{ margin: 0 }}>Reschedule booking</h3>
            <div className="modal-sub">
              {booking.customer?.name} · {booking.service?.name} · {fmtMoney(booking.service?.price)}
            </div>
          </div>
          <button className="bdp-close" onClick={onClose} aria-label="Close" data-testid="reschedule-close">
            <X size={18} />
          </button>
        </div>

        <div className="field">
          <label>Barber</label>
          <select
            className="inp"
            value={selectedBarberId}
            onChange={(e) => setSelectedBarberId(e.target.value)}
            data-testid="reschedule-barber"
          >
            {barbers.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Date</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn s" onClick={goPrevDay} data-testid="reschedule-prev-day">‹</button>
            <div style={{ flex: 1, textAlign: 'center', fontSize: 13, fontWeight: 600 }} data-testid="reschedule-date-label">
              {fmtDate(selectedDate)}
            </div>
            <button className="btn s" onClick={goNextDay} data-testid="reschedule-next-day">›</button>
            <button className="btn ghost" onClick={goToday} data-testid="reschedule-today">Today</button>
          </div>
        </div>

        <div className="field">
          <label>Available time — {durationMin} min slot</label>
          {!hours.open && (
            <div style={{ fontSize: 12.5, color: 'var(--mute)', padding: '8px 0' }} data-testid="reschedule-closed">
              This barber is closed on {fmtDate(selectedDate).split(',')[0]}.
            </div>
          )}
          {hours.open && availableStarts.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--mute)', padding: '8px 0' }} data-testid="reschedule-none">
              No free slots on this day. Try another date.
            </div>
          )}
          {hours.open && availableStarts.length > 0 && (
            <div className="reschedule-slots" data-testid="reschedule-slots">
              {availableStarts.map(m => {
                const isCurrent =
                  selectedBarberId === booking.barber_id
                  && toISODate(selectedDate) === booking.starts_at.slice(0, 10)
                  && m === (parseWallMin(booking.starts_at.slice(11, 16)));
                return (
                  <button
                    key={m}
                    type="button"
                    className={`slot-btn${selectedStartMin === m ? ' on' : ''}${isCurrent ? ' current' : ''}`}
                    onClick={() => setSelectedStartMin(m)}
                    data-testid={`reschedule-slot-${m}`}
                  >
                    {formatWallMin(m)}
                    {isCurrent && <span className="slot-tag">Current</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {error && (
          <div style={{ fontSize: 12.5, color: '#b91c1c', margin: '4px 0 12px' }} data-testid="reschedule-error">
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
          <button
            className="btn ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="reschedule-cancel-btn"
          >Cancel</button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={busy || availableStarts.length === 0}
            data-testid="reschedule-submit"
          >{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
        {confirmNode}
      </div>
    </div>,
    document.body
  );
};

export default RescheduleModal;
